/**
 * Dev-only numeric overrides, read from `sessionStorage`.
 *
 * Why this exists: the session-timeout behaviors are all on production clocks —
 * 15 minutes to idle out, ~an hour before an access token is near enough to
 * expiry for the renewal race to appear. The documented way to exercise them was
 * *"temporarily lower the constant … revert before merging"*, which is a
 * forcing-function failure waiting to happen on a HIPAA control measure: the
 * revert is a promise, not a mechanism, and the shipped value is a
 * §164.312(a)(2)(iii) auto-logoff threshold.
 *
 * These overrides replace that. Nothing is edited, so nothing can be forgotten,
 * and they are **inert in any production build** — `NODE_ENV` is `production`
 * there, so the fallback is the only value that can reach a deployed
 * environment. `NODE_ENV` is read per call (not captured at module load) so that
 * guarantee is testable rather than asserted.
 *
 * Usage — in the browser console, then reload:
 *
 *   sessionStorage.setItem('atman.dev.inactivityThresholdMs', '30000')
 *   sessionStorage.setItem('atman.dev.warningDurationMs', '10000')
 *   sessionStorage.setItem('atman.dev.renewalCheckIntervalMs', '2000')
 *   sessionStorage.setItem('atman.dev.expireEarlySeconds', '3570')
 *
 * A missing, unparseable, or non-positive value falls back — a typo must never
 * silently disable a timeout.
 */

const PREFIX = 'atman.dev.'

export const devTimingEnabled = (): boolean => process.env.NODE_ENV !== 'production'

export const devTiming = (key: string, fallback: number): number => {
  if (!devTimingEnabled()) return fallback
  let raw: string | null = null
  try {
    raw = window.sessionStorage.getItem(`${PREFIX}${key}`)
  } catch {
    // sessionStorage can throw in locked-down / sandboxed contexts.
    return fallback
  }
  if (raw === null) return fallback
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    console.warn(
      `[devTiming] ignoring ${PREFIX}${key}=${JSON.stringify(raw)} — not a positive number; using ${fallback}`
    )
    return fallback
  }
  console.warn(`[devTiming] ${PREFIX}${key} overriding ${fallback} -> ${parsed}`)
  return parsed
}
